/* eslint-disable @typescript-eslint/no-explicit-any */
import { configureStore, Reducer } from '@reduxjs/toolkit';
import { IApplicationContext } from 'app/context/types';
import createSagaMiddleware from 'redux-saga';
import { RootReducer } from './rootReducer';
import { RootSaga } from './rootSagas';
import { IStateModule } from './types';

export class GlobalState {
	private static instance: GlobalState;

	constructor(
		private applicationContext: IApplicationContext,
		private activeModules: IStateModule[],
		private store?: any,
		private sagaMiddleware?: any,
		private additionnalMiddleware?: any,
	) {
		if (applicationContext) {
			this.applicationContext = applicationContext;
		} else {
			console.error(
				'GlobalStore::constructor',
				'Unable to bootstrap the app correctly. Please provide an ApplicationContext instance to your Store Creator',
			);
		}
		if (sagaMiddleware) {
			this.sagaMiddleware = sagaMiddleware;
		} else {
			this.createSagaMiddleware();
		}
		if (store) {
			this.store = store;
		} else {
			this.createStore();
		}
	}

	public static getInstance(
		applicationContext: IApplicationContext,
		activeModules: IStateModule[],
		store?: any,
		sagaMiddleware?: any,
		additionnalMiddleware?: any,
	): GlobalState {
		if (!GlobalState.instance) {
			GlobalState.instance = new GlobalState(
				applicationContext,
				activeModules,
				store,
				sagaMiddleware,
				additionnalMiddleware,
			);
		}

		return GlobalState.instance;
	}

	public static getStore() {
		return this.instance.getStore();
	}

	getActiveModules(): IStateModule[] {
		return this.activeModules;
	}

	setActiveModules(activeModule: IStateModule[]): IStateModule[] {
		return (this.activeModules = activeModule);
	}

	createSagaMiddleware(): any {
		const initialContext: any = {
			dispatch: () => {
				return null;
			},
			applicationContext: this.getApplicationContext(),
		};

		this.sagaMiddleware = createSagaMiddleware(initialContext);
	}

	createRootReducer(): Reducer {
		return new RootReducer(this.getActiveModules()).getRootReducer();
	}

	createRootSagas(): any {
		return new RootSaga(this.getActiveModules()).getRootSagas();
	}

	createStore(): void {
		this.createSagaMiddleware();
		this.store = configureStore({
			reducer: this.createRootReducer(),
			middleware: getDefaultMiddleware =>
				getDefaultMiddleware({
					serializableCheck: false,
				}).concat(this.sagaMiddleware),
		});
		// Overrides the mocked dispatch function after creating the store
		this.sagaMiddleware.setContext({
			dispatch: this.store.dispatch,
			applicationContext: this.getApplicationContext(),
		});
		this.sagaMiddleware.run(this.createRootSagas());
	}

	getStore(): any {
		return this.store;
	}

	getDispatch(): any {
		return this.store.dispatch;
	}

	getSagaMiddleware(): any {
		return this.sagaMiddleware;
	}

	getApplicationContext(): IApplicationContext {
		return this.applicationContext;
	}
}
